import React, { useEffect, useCallback } from 'react';
import useStyles from '../styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import * as IconMDI from 'mdi-material-ui';
import { PasswordFeedback } from '@elevator/ui';
import { TextField, isValidPassword, notTooGuessable } from '@elevator/ui';
import { useFormikContext } from 'formik';
import { ChangePasswordFormIndexProps } from '.';

/**
 * ChangePasswordForm Component
 *
 * Form component for changing user password. Displays fields for current password,
 * new password, and password confirmation. Includes password strength validation
 * and feedback.
 *
 * Features:
 * - Current password field with forgot password link and popover help.
 * - New password field with password strength feedback and validation indicators.
 * - Confirm password field with validation.
 * - Mobile-responsive layout (fullScreen dialog on small viewports via parent).
 * - Integrates with Formik context for form state management.
 *
 * Used in:
 * - `AccountSettings` component as a modal dialog.
 *
 * @param props - Component props
 * @param props.togglePasswordModalOpen - Callback function to close the password change modal.
 */
const ChangePasswordForm = (props: ChangePasswordFormProps): React.JSX.Element => {
  const { togglePasswordModalOpen } = props;

  const {
    values,
    touched,
    errors,
    handleSubmit,
    isValid,
    setFieldTouched,
    setFieldValue,
    isSubmitting,
    validateForm,
  } = useFormikContext<ChangePasswordFormikValues>();

  const { classes } = useStyles();

  // Validate form when values change
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  /**
   * Handler function for field changes in the formik form
   *
   * Sets the new state value and marks the field as "touched" for validation purposes.
   *
   * @param fieldName - Name of the field being changed
   * @param event - Change event from the input
   */
  const handleInputChange = (
    fieldName: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setFieldValue(fieldName, event.target.value);
    setFieldTouched(fieldName, true);
  };

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  /**
   * Opens the popover for forgot password help
   *
   * @param event - Mouse event that triggered the popover
   */
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleForgotHintKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    }
  };

  /**
   * Closes the popover for forgot password help
   */
  const handlePopoverClose = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const showPopover = Boolean(anchorEl);

  /**
   * Renders the dialog header with title and close button.
   * Uses DialogTitle (standard dialog pattern) instead of AppBar.
   */
  const renderDialogHeader = (): React.JSX.Element => (
    <Box className={classes.dialogHeaderRow}>
      <DialogTitle id="change-password-dialog-title">Change Password</DialogTitle>
      <IconButton
        onClick={togglePasswordModalOpen}
        aria-label="close"
        size="small"
        edge="end"
      >
        <IconMDI.Close />
      </IconButton>
    </Box>
  );

  /**
   * Renders the checkmark icon for valid password fields
   *
   * @returns InputAdornment with checkmark icon
   */
  const renderPasswordFieldEndAdornment = (): React.JSX.Element => (
    <InputAdornment position="end">
      <Box paddingRight={1}>
        <IconMDI.Check fontSize="small" className={classes.checkmark} />
      </Box>
    </InputAdornment>
  );

  return (
    <>
      {renderDialogHeader()}
      <Box paddingBottom={2} paddingX={2}>
        <form onSubmit={handleSubmit}>
          <DialogContent className={classes.dialogueContentContainer}>
            <Box>
              <TextField
                id="oldPassword"
                label="Current Password"
                type="password"
                autoComplete="current-password"
                fullWidth
                required
                value={values.oldPassword}
                onChange={(event) => handleInputChange('oldPassword', event)}
                error={touched.oldPassword && Boolean(errors.oldPassword)}
                helperText={touched.oldPassword ? errors.oldPassword : ''}
                disabled={isSubmitting}
              />
              <ClickAwayListener onClickAway={handlePopoverClose}>
                <Box
                  component="span"
                  className={classes.forgotPasswordHint}
                  onClick={handlePopoverOpen}
                  onMouseEnter={handlePopoverOpen}
                  onMouseLeave={handlePopoverClose}
                  onKeyDown={handleForgotHintKeyDown}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={showPopover}
                  aria-label="Help: forgot current password"
                >
                  <IconMDI.InformationOutline
                    className={classes.forgotPasswordHintIcon}
                    aria-hidden
                  />
                  <Typography variant="overline2" component="span" color="inherit">
                    I forgot my current password. What do I do?
                  </Typography>
                </Box>
              </ClickAwayListener>
              <Popover
                open={showPopover}
                anchorEl={anchorEl}
                className={classes.popover}
                classes={{
                  paper: classes.popoverContent,
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
              >
                <Typography variant="overline2">
                  To change your password without providing your current password, you
                  will need to signout and request a reset password code. Your email must
                  be verified.
                </Typography>
              </Popover>
            </Box>
            <Box>
              <TextField
                id="newPassword"
                label="New Password"
                type="password"
                autoComplete="new-password"
                fullWidth
                required
                value={values.newPassword}
                onChange={(event) => handleInputChange('newPassword', event)}
                error={touched.newPassword && Boolean(errors.newPassword)}
                helperText={<PasswordFeedback password={values.newPassword} />}
                disabled={isSubmitting}
                InputProps={{
                  endAdornment:
                    isValidPassword(values.newPassword) &&
                    notTooGuessable(values.newPassword) &&
                    renderPasswordFieldEndAdornment(),
                }}
              />
            </Box>
            <Box>
              <TextField
                id="confirmPassword"
                label="Confirm Password"
                type="password"
                autoComplete="new-password"
                fullWidth
                required
                value={values.confirmPassword}
                onChange={(event) => handleInputChange('confirmPassword', event)}
                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                helperText={touched.confirmPassword ? errors.confirmPassword : ''}
                disabled={isSubmitting}
                InputProps={{
                  endAdornment:
                    touched.confirmPassword &&
                    !errors.confirmPassword &&
                    renderPasswordFieldEndAdornment(),
                }}
              />
            </Box>
            <Box>
              <Button
                fullWidth
                size="large"
                type="submit"
                disabled={!isValid || isSubmitting}
                color="primary"
                variant="contained"
              >
                Update Password
              </Button>
            </Box>
          </DialogContent>
        </form>
      </Box>
    </>
  );
};

export type ChangePasswordFormProps = ChangePasswordFormIndexProps;

export interface ChangePasswordFormikValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default ChangePasswordForm;
